import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon from '../../Icon';
import { LiliLogoNew } from '../../../assets/svg';
import Heading from '../../Heading';
import DigitsContainer from '../../DigitsContainer';
import RememberMfaLogin from '../../../flows/login/components/RememberMfaLogin';
import { MfaContext } from '../index';
import { ResendWrapper } from './ResendWrapper';
import { useVerifyAchOut } from '../../../hooks/mutations/useVerifyAchOut';
import { DigitsNumAccordingToType, IMfaErrorData, MfaActionType, MfaType } from '../types';
import { UserMessage } from '../../../DesignSystem/LiliUserMessage/UserMessage';
import Button from '../../Button';
import ErrorIcon from '../../../assets/icons/svg/stamp-icons/ic-stamp-alert.svg';
import { VIEWS_MAP } from '../../../router/constants';
import { MFA_TEXTS, STYLES } from '../helpers/constants';

export const MfaIntro = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [localError, setLocalError] = useState<IMfaErrorData | null>(null);
  const [shouldShowGenericError, setShouldShowGenericError] = useState<boolean>(false);
  const {
    isTrustSelected,
    setIsTrustSelected,
    reqOtpPayload,
    onSuccess,
    setActiveStep,
    onExternalFlowError,
    contextType,
    billId,
  } = useContext(MfaContext)!;

  const {
    mutateAsync: verifyAchOut,
    isError,
    isLoading,
  } = useVerifyAchOut({
    onVerifyError: (errorData) => {
      if (errorData?.rawErrorData && errorData.isExternalFlowError) onExternalFlowError(errorData?.rawErrorData);
      if (errorData?.stepToSwitch) setActiveStep(errorData.stepToSwitch);
      if (errorData?.shouldShowGenericError) {
        setShouldShowGenericError(true);
      }
      if (errorData.shouldShowLocalErrorState) {
        setLocalError(errorData);
      }
    },
    contextType,
    billId,
  });

  const { mfaActionData, mfaType } = reqOtpPayload;

  async function handleFulfill(answer: string) {
    switch (mfaActionData?.actionType) {
      case MfaActionType.ACH_OUT:
        verifyAchOut({ payload: { ...mfaActionData, answer, mfaType } }).then((achOutRes) => onSuccess(achOutRes));
        break;

      default:
        console.error('');
    }
  }

  const numberOfDigits = useMemo(
    () => (reqOtpPayload.mfaType === MfaType.SMS ? DigitsNumAccordingToType.SMS : DigitsNumAccordingToType.VOICE),
    [reqOtpPayload],
  );

  return (
    <div className='flex flex-col py-[40px] px-[40px] bg-bw00 rounded-[6px]'>
      {shouldShowGenericError ? (
        <UserMessage onClick={() => {}} id='genericErrorComponent'>
          <div className='py-[24px] bg-bw00 px-[24px] max-w-[400px] rounded-[8px] border border-bw03 relative flex flex-col items-center justify-center fadeIn'>
            <Icon className='w-[80px] my-[40px]' src={ErrorIcon} />
            <Heading
              title={t('ACH_OUT_ERROR_HEADERS.GENERIC_ERROR_TITLE')}
              className='font-metricR text-[24px] text-bw08 mb-[8px]'
            />
            <Heading
              title={t('ACH_OUT_ERROR_HEADERS.GENERIC_ERROR_SUB_TITLE')}
              className='font-metricR text-center text-[16px] text-bw07 mb-[100px]'
            />
            <Button
              onClick={() => navigate(`/${VIEWS_MAP.DASHBOARD}`)}
              className='w-[280px] text-bw00 bg-action03 hover:bg-action04 py-[17px] rounded-[4px] font-metricR text-[16px]'
              title={t('MAIL_CHECK.LIMIT_REACHED.CTA')}
              id='limitReachConfirmation'
            />
          </div>
        </UserMessage>
      ) : (
        <div>
          <Icon className='w-[48px]  h-[32px] mb-[24px]' src={LiliLogoNew} />
          <Heading title='Verification Code' className='font-metricR text-bw08 text-[25px] mb-[8px]' />
          <Heading
            title={`${t('ACH_OUT_MFA.VERIFICATION_TITLE')} ${mfaActionData?.mfaDestination}`}
            className='font-metricR text-bw07 text-[16px] w-[370px]'
          />
          <DigitsContainer
            numOfDigits={numberOfDigits}
            onFulfill={(ans: string) => handleFulfill(ans)}
            isLoading={isLoading}
            isError={isError}
          />
          {localError && localError.shouldShowLocalErrorState && (
            <p className={STYLES.ERROR_TEXT}>{localError?.errorMessage}</p>
          )}
          <RememberMfaLogin
            customTitle={MFA_TEXTS.CUSTOM_CHECKBOX_TITLE}
            checkboxValue={isTrustSelected}
            handleCheck={(isChecked: boolean) => setIsTrustSelected(isChecked)}
          />
          <ResendWrapper onErrorData={(error) => setLocalError(error)} />
        </div>
      )}
    </div>
  );
};
